import { useState, useContext } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Icon } from "@iconify/react";
import { UserContext } from "../context/user";
import { Get, Post } from "../api/api";
import { useQuery } from "@tanstack/react-query";
import { Loading } from "./Loading";
import { Calculator } from "./Calculator";
import { SaleConfirmed } from "./SaleConfirmed";
import { printTicket } from "../api/printer";
import { GenerateTicket } from "../api/ticket";
import toast from "react-hot-toast";
import { cn } from "@/lib/utils";

const money = (n) => Math.round(n * 100) / 100;

function mergeIntoBatch(list, product, qty) {
  const idx = list.findIndex(b => b.custom_id === product.custom_id);
  if (idx === -1) {
    return [...list, {
      custom_id: product.custom_id,
      name: product.name,
      custom_price: product.custom_price,
      quantity: qty
    }];
  }
  const next = [...list];
  next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
  return next;
}

export function CheckoutContent({ items = [], onClose, searchClient, client, onSold = () => { } }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sold, setSold] = useState(null);
  const [multiMode, setMultiMode] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [payments, setPayments] = useState([]);
  const [batch, setBatch] = useState([]);
  const [change, setChange] = useState(null);
  const [cashChanges, setCashChanges] = useState([]);
  const [calcOpen, setCalcOpen] = useState(false);
  const { shop } = useContext(UserContext);

  const { isLoading: isLoadingPagos, data: tiposPago } = useQuery({
    queryKey: ["tiposdepago"],
    queryFn: () => Get({ request: "/pay_method?shop_id=" + localStorage.getItem("shop_id") }),
  });

  if (isLoadingPagos || loading) return <Loading />;

  const total = money(items.reduce((acc, p) => acc + (p.custom_price * p.quantity), 0));
  const selectedMethod = tiposPago.find((m) => m.id == paymentMethod) || null;

  const paidQtyOf = (customId) => payments.filter(p => p.custom_id === customId).reduce((acc, p) => acc + p.quantity, 0);
  const batchQtyOf = (customId) => batch.filter(b => b.custom_id === customId).reduce((acc, b) => acc + b.quantity, 0);
  const batchTotal = money(batch.reduce((acc, b) => acc + (b.quantity * b.custom_price), 0));
  const paidAmount = money(payments.reduce((acc, p) => acc + p.amount, 0));
  const pendingAmount = money(total - paidAmount);
  const allPaid = items.length > 0 && items.every(p => paidQtyOf(p.custom_id) >= p.quantity);

  const availableOf = (product) => product.quantity - paidQtyOf(product.custom_id) - batchQtyOf(product.custom_id);

  const toggleMode = (enabled) => {
    setMultiMode(enabled);
    setPaymentMethod(null);
    setPayments([]);
    setBatch([]);
    setChange(null);
    setCashChanges([]);
    setCalcOpen(false);
  };

  const selectMethod = (method) => {
    if (paymentMethod == method.id) {
      setPaymentMethod(null);
      setCalcOpen(false);
      setChange(null);
      return;
    }
    setPaymentMethod(method.id);
    setCalcOpen(!!method.change);
    if (!method.change) setChange(null);
  };

  const addToBatch = (product, qty) => {
    const available = availableOf(product);
    if (available <= 0) return;
    setBatch(prev => mergeIntoBatch(prev, product, Math.min(qty, available)));
  };

  const removeFromBatch = (customId, qty) => {
    setBatch(prev => {
      const idx = prev.findIndex(b => b.custom_id === customId);
      if (idx === -1) return prev;
      const next = [...prev];
      if (next[idx].quantity <= qty) {
        next.splice(idx, 1);
      } else {
        next[idx] = { ...next[idx], quantity: next[idx].quantity - qty };
      }
      return next;
    });
  };

  const clearBatch = () => setBatch([]);

  const acceptBatch = () => {
    if (batch.length === 0) {
      toast.error("Añade artículos a la lista de cobro");
      return;
    }
    if (!selectedMethod) {
      toast.error("Selecciona un método de pago");
      return;
    }
    setPayments(prev => [...prev, ...batch.map(b => ({
      id: Date.now() + Math.random() + Math.random(),
      custom_id: b.custom_id,
      quantity: b.quantity,
      amount: money(b.quantity * b.custom_price),
      methodId: selectedMethod.id
    }))]);
    if (selectedMethod.change && typeof change === "number") {
      setCashChanges(prev => [...prev, { entregado: money(change + batchTotal), change: money(Math.max(0, change)) }]);
      setChange(null);
    }
    setBatch([]);
    toast.success("Artículos marcados como pagados");
  };

  const undoItem = (customId) => {
    setPayments(prev => {
      const idx = prev.map(p => p.custom_id).lastIndexOf(customId);
      if (idx === -1) return prev;
      const next = [...prev];
      next.splice(idx, 1);
      return next;
    });
  };

  const finishSell = async () => {
    let pagos;
    let saleChange = null;

    if (multiMode) {
      if (!allPaid) {
        toast.error("Quedan artículos por cobrar");
        return;
      }
      const byMethod = payments.reduce((acc, p) => {
        acc[p.methodId] = money((acc[p.methodId] || 0) + p.amount);
        return acc;
      }, {});
      pagos = Object.keys(byMethod).map(type_id => ({ type_id: Number(type_id), amount: byMethod[type_id] }));
      saleChange = cashChanges.length > 0 ? money(cashChanges.reduce((acc, c) => acc + c.change, 0)) : null;
    } else {
      if (!paymentMethod) {
        toast.error("Selecciona un método de pago");
        return;
      }
      pagos = [{ type_id: paymentMethod, amount: total }];
      saleChange = typeof change === "number" ? Math.max(0, change) : null;
    }

    setLoading(true);

    let data = {
      pay_method: paymentMethod || pagos[0].type_id,
      pagos: JSON.stringify(pagos),
      products: JSON.stringify(items),
      shop_id: localStorage.getItem("shop_id"),
      exercise_id: localStorage.getItem("exercise_id"),
      total: String(money(total))
    };

    if (client?.id) data.client_id = client.id;

    let r = await Post({ request: "/tickets", data });
    setLoading(false);

    if (r.id) {
      setSuccess(true);
      setSold({
        ticket: r,
        method: selectedMethod || (pagos.length === 1 ? tiposPago.find(m => m.id == pagos[0].type_id) : null),
        change: saleChange,
        cashPayments: multiMode ? cashChanges : [],
        client: client || null
      });
      setPaymentMethod(null);
      setPayments([]);
      setBatch([]);
      setChange(null);
      setCashChanges([]);
      printTicket(GenerateTicket(r, shop?.ticket_settings?.paper_saving || false));
      onSold();
    } else {
      toast.error("Ha ocurrido un error");
    }
  };

  if (success) {
    return sold ? (
      <SaleConfirmed
        ticket={sold.ticket}
        method={sold.method}
        change={sold.change}
        cashPayments={sold.cashPayments}
        client={sold.client}
        onContinue={() => {
          onClose();
          setTimeout(() => {
            setSuccess(false);
            setSold(null);
            setChange(null);
            setCashChanges([]);
          }, 300);
        }}
      />
    ) : (
      <Loading />
    );
  }

  return (
    <div className="w-full h-full animate-fade animate-once flex gap-4 p-4 select-none">
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-start justify-between mb-3 gap-4">
          <div className="text-xl font-semibold text-neutral-900">
            Artículos a cobrar{items.length > 0 && <span className="text-neutral-400 font-normal"> ({items.length})</span>}
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs text-neutral-500">Subtotal</div>
            <div className="text-2xl font-extrabold text-neutral-900 leading-tight">{total.toFixed(2)}€</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-thumb-rounded-lg scrollbar-track-rounded-lg">
          {items.map((product) => {
            const paid = paidQtyOf(product.custom_id);
            const inBatch = batchQtyOf(product.custom_id);
            const available = availableOf(product);
            const remaining = product.quantity - paid;
            return (
              <div key={product.custom_id} className="bg-white rounded-xl border p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-lg bg-neutral-100 flex items-center justify-center text-xl font-bold text-neutral-700 shrink-0">
                      {product.quantity}
                    </div>
                    <div className="min-w-0">
                      <div className="text-lg font-semibold text-neutral-900 truncate">{product.name}</div>
                      <div className="text-sm text-neutral-500">{product.custom_price.toFixed(2)}€/ud</div>
                      {(product.custom_mods || []).length > 0 && (
                        <div className="text-sm text-neutral-500 mt-0.5">
                          {(product.custom_mods || []).map((mod, i) => (
                            <div key={i}>+ {mod.defaultEnabled ? "Sin " : "Con "}{mod.name}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-2xl font-bold text-neutral-900">{(product.custom_price * product.quantity).toFixed(2)}€</div>
                    {multiMode && (
                      <div className={cn("text-sm font-medium", remaining === 0 ? "text-green-600" : "text-neutral-400")}>
                        {paid} de {product.quantity} pagado{inBatch > 0 && ` · ${inBatch} en lista`}
                      </div>
                    )}
                  </div>
                </div>

                {multiMode && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                    {paid > 0 && (
                      <Button size="sm" variant="outline" onClick={() => undoItem(product.custom_id)}>
                        <Icon icon="mdi:undo" className="mr-1 text-base" />
                        Deshacer
                      </Button>
                    )}
                    <Button size="sm" variant="default" disabled={available === 0} onClick={() => addToBatch(product, 1)} className="ml-auto">
                      <Icon icon="mdi:plus" className="mr-1 text-base" />
                      Pagar ahora
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="bg-white rounded-xl border p-6 text-center text-lg text-neutral-400">
              No hay artículos en la venta
            </div>
          )}
        </div>
      </div>

      <div className="w-[400px] flex flex-col gap-3 h-full">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => toggleMode(false)}
            className={cn(
              "cursor-pointer rounded-xl border bg-white p-3 flex flex-col items-center gap-1.5 transition-colors active:scale-95",
              !multiMode ? "border-blue-500 ring-2 ring-blue-100" : ""
            )}
          >
            <Icon icon="mdi:cash" className="text-3xl text-neutral-500" />
            <div className="text-base font-semibold text-neutral-800">Pago único</div>
          </button>
          <button
            onClick={() => toggleMode(true)}
            className={cn(
              "cursor-pointer rounded-xl border bg-white p-3 flex flex-col items-center gap-1.5 transition-colors active:scale-95",
              multiMode ? "border-blue-500 ring-2 ring-blue-100" : ""
            )}
          >
            <Icon icon="mdi:format-list-checkbox" className="text-3xl text-neutral-500" />
            <div className="text-base font-semibold text-neutral-800">Múltiples productos</div>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-thumb-rounded-lg scrollbar-track-rounded-lg">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-neutral-500">Total</div>
                <div className="text-4xl font-extrabold text-neutral-900 leading-none">{total.toFixed(2)}€</div>
              </div>
              {multiMode && (
                <div className="flex items-center justify-between gap-4 mt-1.5">
                  <div className="text-sm text-neutral-500">Pagado</div>
                  <div className="text-xl font-bold text-green-600">{paidAmount.toFixed(2)}€</div>
                </div>
              )}
              {multiMode && (
                <div className="flex items-center justify-between gap-4 mt-1">
                  <div className="text-sm text-neutral-500">Pendiente</div>
                  <div className={cn("text-xl font-bold", pendingAmount > 0 ? "text-amber-600" : "text-green-600")}>
                    {pendingAmount.toFixed(2)}€
                  </div>
                </div>
              )}
              <div className="flex items-center justify-center gap-2 mt-3 cursor-pointer" onClick={() => searchClient(true)}>
                <Icon icon="material-symbols:person" className="text-xl text-blue-600" />
                <div className="text-base font-medium text-neutral-700 truncate">{client?.name || "Público en general"}</div>
              </div>
            </CardContent>
          </Card>

          {multiMode && (
            <Card className={cn(batchTotal > 0 && "border-blue-300 bg-blue-50/40")}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <Icon icon="mdi:clipboard-text-clock" className="text-lg text-blue-600" />
                    <div className="text-base font-semibold text-neutral-900">A cobrar ahora</div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={clearBatch} disabled={batch.length === 0}>
                    Limpiar
                  </Button>
                </div>
                {batch.length === 0 ? (
                  <div className="text-sm text-neutral-400 py-2">
                    Añade artículos con &quot;Pagar ahora&quot;
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {batch.map((b) => (
                      <div key={b.custom_id} className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center text-base font-bold text-neutral-700 shrink-0">
                            {b.quantity}
                          </div>
                          <span className="text-base text-neutral-800 truncate">{b.name}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-base font-bold text-neutral-900">{(b.quantity * b.custom_price).toFixed(2)}€</span>
                          <button
                            onClick={() => removeFromBatch(b.custom_id, 1)}
                            className="w-7 h-7 rounded-md border flex items-center justify-center text-neutral-500 hover:text-red-500 transition-colors"
                          >
                            <Icon icon="mdi:minus" className="text-lg" />
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-2 mt-1.5 border-t">
                      <div className="text-sm text-neutral-500">Subtotal lote</div>
                      <div className="text-2xl font-extrabold text-neutral-900 leading-none">{batchTotal.toFixed(2)}€</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="text-sm text-neutral-500">Método de pago</div>
          <div className="grid grid-cols-2 gap-2">
            {tiposPago.map((method) => {
              const active = paymentMethod == method.id;
              return (
                <div
                  key={method.id}
                  onClick={() => selectMethod(method)}
                  className={cn(
                    "cursor-pointer rounded-xl border bg-white p-3 flex flex-col items-center gap-1.5 transition-colors active:scale-95",
                    active ? "border-blue-500 ring-2 ring-blue-100" : ""
                  )}
                >
                  <Icon icon={method.icon} className="text-4xl text-neutral-500" />
                  <div className="text-base font-semibold text-neutral-800">{method.name}</div>
                </div>
              );
            })}
          </div>

          {selectedMethod?.change && !calcOpen && (
            <Button variant="outline" className="w-full h-11 text-base font-semibold" size="lg" onClick={() => setCalcOpen(true)}>
              <Icon icon="mdi:calculator-variant" className="mr-2 text-xl text-blue-600" />
              Calcular cambio
            </Button>
          )}
        </div>

        {multiMode && !allPaid && (
          <Button className="w-full h-12 text-lg font-bold" variant="default" size="lg" onClick={acceptBatch}>
            <Icon icon="mdi:check-decagram" className="mr-2 text-xl" />
            Aceptar ({batchTotal.toFixed(2)}€)
          </Button>
        )}

        <Button
          className="w-full h-14 text-xl font-bold"
          variant="default"
          size="lg"
          onClick={finishSell}
          disabled={items.length == 0 || (multiMode && !allPaid)}
        >
          <Icon icon="mdi:cash-check" className="mr-2 text-2xl" />
          {multiMode ? `Finalizar venta (${allPaid ? paidAmount.toFixed(2) + "€" : "falta " + pendingAmount.toFixed(2) + "€"})` : "Finalizar venta"}
        </Button>
      </div>

      <Dialog open={calcOpen} onOpenChange={setCalcOpen}>
        <DialogContent
          forceMount
          className={cn(
            "flex w-[min(30rem,calc(100vw-1.5rem))] max-w-none max-h-[calc(100dvh-1.5rem)] flex-col gap-3 overflow-hidden p-3 sm:p-4",
            !calcOpen && "hidden"
          )}
        >
          <DialogTitle className="shrink-0 text-base sm:text-lg">Calcular cambio</DialogTitle>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-neutral-50 rounded-2xl p-2 sm:p-4">
            <Calculator total={multiMode ? batchTotal : total} setChange={setChange} />
          </div>
          <div className="shrink-0 -mx-3 sm:-mx-4 -mb-3 sm:-mb-4 px-3 sm:px-4 py-3 bg-white border-t flex items-center justify-between gap-3">
            <div>
              <div className="text-xs sm:text-base text-neutral-500">Cambio</div>
              <div className={cn("font-extrabold leading-tight text-[clamp(1.5rem,4dvh,1.875rem)]", typeof change === "number" ? (change >= 0 ? "text-green-600" : "text-red-500") : "text-neutral-900")}>
                {typeof change === "number" ? change.toFixed(2) : "0.00"}€
              </div>
            </div>
            <DialogClose asChild>
              <Button className="h-[clamp(2.75rem,7dvh,3.5rem)] px-5 sm:px-8 text-base sm:text-lg font-bold" size="lg">
                <Icon icon="mdi:check" className="mr-1 sm:mr-2 text-xl sm:text-2xl" />
                Aceptar
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}